import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { WhatsAppLink } from "@/components/WhatsApp";
import { newsletterSubscribe } from "@/lib/api";

const NewsletterSignup = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Inserisci un'email valida"); return; }
        setLoading(true);
        try {
            await newsletterSubscribe(email);
            setDone(true);
            toast.success("Iscrizione completata · a presto!");
        } catch { toast.error("Iscrizione non riuscita, riprova"); }
        finally { setLoading(false); }
    };
    return (
        <div data-testid="newsletter-signup" className="max-w-md mx-auto px-5 mb-8">
            <div className="text-[11px] tracking-[0.25em] text-[#F4F2ED] mb-3">RESTA AGGIORNATO SUI PROSSIMI EVENTI</div>
            {done ? (
                <div data-testid="newsletter-signup-done" className="text-[11px] tracking-[0.15em] text-[#16a34a] normal-case">Grazie! Ti avviseremo sul prossimo evento VELORA JDM.</div>
            ) : (
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
                    <input data-testid="newsletter-signup-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="La tua email" required
                        className="flex-1 bg-[#15181C] border border-[#9AA2AD]/30 px-3 py-2.5 text-[#F4F2ED] text-sm tracking-normal normal-case focus:border-[#E2231A] outline-none" />
                    <button data-testid="newsletter-signup-submit" type="submit" disabled={loading}
                        className="bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-50 text-[#F4F2ED] px-5 py-2.5 text-xs uppercase tracking-widest transition-colors">
                        {loading ? "Invio…" : "Iscriviti"}
                    </button>
                </form>
            )}
            {!done && (
                <div className="text-[9px] text-[#9AA2AD]/70 mt-2 normal-case tracking-normal leading-relaxed">
                    Cliccando "Iscriviti" acconsenti a ricevere comunicazioni da VELORA JDM e accetti la <Link to="/privacy" className="underline hover:text-[#E2231A]">Privacy Policy</Link>. Puoi disiscriverti quando vuoi.
                </div>
            )}
        </div>
    );
};

export const Hinomaru = ({ size = 32 }) => (
    <span
        data-testid="hinomaru-logo"
        aria-label="VELORA JDM"
        className="inline-block rounded-full shrink-0"
        style={{
            width: size,
            height: size,
            background: "#E2231A",
            boxShadow: `0 0 ${size / 2}px rgba(226,35,26,0.55)`,
        }}
    />
);

const LangToggle = ({ compact }) => {
    const { lang, setLang } = useLang();
    const other = lang === "it" ? "en" : "it";
    return (
        <button
            type="button"
            data-testid={compact ? "lang-toggle-mobile" : "lang-toggle"}
            onClick={() => setLang(other)}
            title={lang === "it" ? "Switch to English" : "Passa all'italiano"}
            className={`group flex items-center gap-1 border border-[#9AA2AD]/30 hover:border-[#E2231A] text-[10px] uppercase tracking-[0.25em] transition-colors duration-150 ${compact ? "px-2 py-1" : "px-2.5 py-1.5"}`}
        >
            <span className={lang === "it" ? "text-[#E2231A]" : "text-[#9AA2AD]"}>IT</span>
            <span className="text-[#9AA2AD]/40">/</span>
            <span className={lang === "en" ? "text-[#E2231A]" : "text-[#9AA2AD]"}>EN</span>
        </button>
    );
};

const scrollToProdotti = (e) => {
    if (window.location.pathname === "/") {
        e.preventDefault();
        document.getElementById("prodotti")?.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", "/#prodotti");
    }
};

const scrollToChiSiamo = (e) => {
    if (window.location.pathname === "/") {
        e.preventDefault();
        document.getElementById("chi-siamo")?.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", "/#chi-siamo");
    }
};

export const Nav = () => {
    const { t } = useLang();
    const [open, setOpen] = useState(false);

    // close drawer on route change / hash change
    useEffect(() => {
        const close = () => setOpen(false);
        window.addEventListener("hashchange", close);
        return () => window.removeEventListener("hashchange", close);
    }, []);

    // lock body scroll while drawer open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    return (
        <header className="w-full border-b border-[#9AA2AD]/15 bg-[#15181C]/95 backdrop-blur sticky top-0 z-40">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
                <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2 md:gap-3 min-w-0">
                    <img src="/logo/velora-horizontal.png" alt="VELORA JDM" className="h-9 md:h-11 w-auto select-none" draggable={false} data-testid="nav-logo" />
                </Link>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.2em]">
                    <Link data-testid="nav-book-link" to="/#prodotti" onClick={scrollToProdotti} className="hover:text-[#E2231A] transition-colors duration-150">{t("nav.book")}</Link>
                    <Link data-testid="nav-about-link" to="/#chi-siamo" onClick={scrollToChiSiamo} className="hover:text-[#E2231A] transition-colors duration-150">{t("nav.about")}</Link>
                    <Link data-testid="nav-admin-link" to="/admin" className="text-[#9AA2AD] hover:text-[#F4F2ED] transition-colors duration-150">{t("nav.admin")}</Link>
                    <LangToggle />
                </nav>

                {/* Mobile hamburger */}
                <div className="md:hidden flex items-center gap-2">
                    <LangToggle compact />
                    <button
                        data-testid="nav-hamburger"
                        aria-label="Menu"
                        aria-expanded={open}
                        onClick={() => setOpen(v => !v)}
                        className="p-2 -mr-2 text-[#F4F2ED] hover:text-[#E2231A] transition-colors duration-150"
                    >
                        {open ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div data-testid="nav-drawer" className="md:hidden fixed inset-x-0 top-[57px] bottom-0 z-50 bg-[#15181C] border-t border-[#9AA2AD]/15 overflow-y-auto">
                    <nav className="flex flex-col px-6 py-8 gap-2">
                        <MobileLink to="/#prodotti" tid="mnav-book" onClick={(e) => { scrollToProdotti(e); setOpen(false); }}>{t("nav.book")}</MobileLink>
                        <MobileLink to="/#chi-siamo" tid="mnav-about" onClick={(e) => { scrollToChiSiamo(e); setOpen(false); }}>{t("nav.about")}</MobileLink>
                        <MobileLink to="/termini" tid="mnav-termini" onClick={() => setOpen(false)}>{t("footer.termini")}</MobileLink>
                        <MobileLink to="/privacy" tid="mnav-privacy" onClick={() => setOpen(false)}>{t("footer.privacy")}</MobileLink>
                        <MobileLink to="/rimborsi" tid="mnav-rimborsi" onClick={() => setOpen(false)}>{t("footer.rimborsi")}</MobileLink>
                        <MobileLink to="/contatti" tid="mnav-contatti" onClick={() => setOpen(false)}>{t("footer.contatti")}</MobileLink>
                        <MobileLink to="/admin" tid="mnav-admin" onClick={() => setOpen(false)}>{t("nav.admin")}</MobileLink>
                        <a href="mailto:info@velorajdm.com" className="mt-8 text-xs uppercase tracking-widest text-[#9AA2AD]">info@velorajdm.com</a>
                    </nav>
                </div>
            )}
        </header>
    );
};

const MobileLink = ({ to, tid, children, onClick }) => (
    <Link data-testid={tid} to={to} onClick={onClick} className="block py-4 border-b border-[#9AA2AD]/10 font-display text-2xl uppercase tracking-widest hover:text-[#E2231A] transition-colors duration-150">
        {children}
    </Link>
);

export const Footer = () => {
    const { t } = useLang();
    return (
        <footer className="mt-24 border-t border-[#9AA2AD]/15 py-10 text-center text-xs uppercase tracking-[0.25em] text-[#9AA2AD]">
            <NewsletterSignup />
            <div className="flex items-center justify-center mb-4">
                <img src="/logo/velora-horizontal-alt.png" alt="VELORA JDM Experience" className="h-10 w-auto select-none" draggable={false} data-testid="footer-logo" />
            </div>
            <div className="max-w-3xl mx-auto px-5 mb-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                <Link data-testid="footer-termini" to="/termini" className="hover:text-[#E2231A] transition-colors duration-150">{t("footer.termini")}</Link>
                <Link data-testid="footer-gallery" to="/gallery" className="hover:text-[#E2231A] transition-colors duration-150">Gallery eventi</Link>
                <Link data-testid="footer-privacy" to="/privacy" className="hover:text-[#E2231A] transition-colors duration-150">{t("footer.privacy")}</Link>
                <Link data-testid="footer-rimborsi" to="/rimborsi" className="hover:text-[#E2231A] transition-colors duration-150">{t("footer.rimborsi")}</Link>
                <Link data-testid="footer-contatti" to="/contatti" className="hover:text-[#E2231A] transition-colors duration-150">{t("footer.contatti")}</Link>
            </div>
            <div className="text-[10px] text-[#9AA2AD]/70 mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                <a href="mailto:info@velorajdm.com" className="hover:text-[#E2231A]">info@velorajdm.com</a>
                <span className="text-[#9AA2AD]/40 hidden sm:inline">·</span>
                <WhatsAppLink tid="footer-whatsapp" showIcon={true} label={t("footer.whatsapp")} className="text-[10px]" />
            </div>
            <div className="text-[10px] text-[#9AA2AD]/60 mb-2">{t("footer.service")}</div>
            {t("footer.tagline")} — © {new Date().getFullYear()}
        </footer>
    );
};
